import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Link2, Trash2, Pencil, Eye, Search, Phone, Mail, Briefcase, MapPin, Cake, Globe2, MoreVertical, KeyRound, UserCheck, UserX } from "lucide-react";
import { toast } from "sonner";
import ExportMenu from "@/components/ExportMenu";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";

const blankTenant = {
  email: "", name: "", password: "", phone: "",
  first_name: "", last_name: "", cell_phone: "", address: "",
  date_of_birth: "", age: "", social_security_number: "", passport_number: "",
  work_place_name: "", work_address: "", work_phone: "", work_salary_hourly: "",
  preferred_language: "", notes: "",
};

const LANGUAGES = ["English", "Spanish", "Portuguese", "French", "Haitian Creole", "Mandarin", "Arabic", "Russian", "Other"];

function fullName(t) {
  const f = (t.first_name || "").trim();
  const l = (t.last_name || "").trim();
  if (f || l) return `${f} ${l}`.trim();
  return t.name || "—";
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [leases, setLeases] = useState([]);
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");

  // Create dialog
  const [open, setOpen] = useState(false);
  const [tForm, setTForm] = useState(blankTenant);

  // Detail / edit dialog
  const [editing, setEditing] = useState(null);  // tenant being edited
  const [eForm, setEForm] = useState(null);
  const [editMode, setEditMode] = useState(false);

  // Lease dialog
  const [leaseOpen, setLeaseOpen] = useState(false);
  const [lForm, setLForm] = useState({ property_id: "", tenant_id: "", monthly_rent: 0, start_date: "", end_date: "", deposit: 0 });

  // Admin actions
  const [pwdTarget, setPwdTarget] = useState(null);   // tenant for password change
  const [pwdValue, setPwdValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const setActive = async (t, active) => {
    try {
      await api.post(`/tenants/${t.id}/${active ? "activate" : "deactivate"}`);
      toast.success(`${fullName(t)} ${active ? "activated" : "deactivated"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed"); }
  };

  const changePassword = async () => {
    if (!pwdValue || pwdValue.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/tenants/${pwdTarget.id}/password`, { password: pwdValue });
      toast.success(`Password updated for ${fullName(pwdTarget)}`);
      setPwdTarget(null); setPwdValue("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed"); }
  };

  const deleteTenant = async () => {
    if (!deleteTarget) return;
    try {
      const { data } = await api.delete(`/tenants/${deleteTarget.id}`);
      toast.success(`${fullName(deleteTarget)} deleted${data.leases_removed ? ` · ${data.leases_removed} lease(s) removed` : ""}`);
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed"); }
  };

  const load = async () => {
    const [t, l, p] = await Promise.all([api.get("/tenants"), api.get("/leases"), api.get("/properties")]);
    setTenants(t.data); setLeases(l.data); setProperties(p.data);
  };
  useEffect(() => { load(); }, []);

  const createTenant = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...tForm };
      if (!payload.name) payload.name = `${payload.first_name} ${payload.last_name}`.trim();
      if (payload.age === "" || payload.age === null) delete payload.age; else payload.age = Number(payload.age);
      if (payload.work_salary_hourly === "" || payload.work_salary_hourly === null) delete payload.work_salary_hourly; else payload.work_salary_hourly = Number(payload.work_salary_hourly);
      await api.post("/tenants", payload);
      toast.success("Tenant created");
      setOpen(false); setTForm(blankTenant); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openTenant = (t) => {
    setEditing(t);
    setEForm({ ...blankTenant, ...t, password: "" });
    setEditMode(false);
  };

  const saveTenant = async () => {
    try {
      const payload = { ...eForm };
      delete payload.password; delete payload.id; delete payload.role; delete payload.created_at; delete payload.created_by; delete payload.updated_at;
      if (payload.age === "" || payload.age === null) delete payload.age; else payload.age = Number(payload.age);
      if (payload.work_salary_hourly === "" || payload.work_salary_hourly === null) delete payload.work_salary_hourly; else payload.work_salary_hourly = Number(payload.work_salary_hourly);
      const { data } = await api.patch(`/tenants/${editing.id}`, payload);
      toast.success("Saved");
      setEditing(data); setEForm({ ...blankTenant, ...data });
      setEditMode(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createLease = async (e) => {
    e.preventDefault();
    try {
      await api.post("/leases", { ...lForm, monthly_rent: Number(lForm.monthly_rent), deposit: Number(lForm.deposit) });
      toast.success("Lease assigned");
      setLeaseOpen(false); setLForm({ property_id: "", tenant_id: "", monthly_rent: 0, start_date: "", end_date: "", deposit: 0 }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const delLease = async (id) => {
    if (!window.confirm("Delete this lease?")) return;
    await api.delete(`/leases/${id}`); toast.success("Removed"); load();
  };

  const exportCsv = async () => {
    // legacy — replaced by ExportMenu
  };

  const filtered = tenants.filter(t => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [t.name, t.email, t.phone, t.first_name, t.last_name, t.cell_phone, t.address, t.work_place_name, t.preferred_language]
      .some(v => (v || "").toString().toLowerCase().includes(s));
  });

  return (
    <AppLayout
      title="Tenants"
      subtitle="Manage client demographics, contact information & lease assignments"
      actions={
        <div className="flex gap-2">
          <ExportMenu
            endpoint="/tenants/export.csv"
            filenameBase="rojasproperty-tenants"
            testidPrefix="export-tenants"
            disabled={tenants.length === 0}
          />
          <Dialog open={leaseOpen} onOpenChange={setLeaseOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-lease-button" variant="outline" className="gap-2"><Link2 className="h-4 w-4" /> Assign Lease</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Assign tenant to property</DialogTitle></DialogHeader>
              <form onSubmit={createLease} className="space-y-4 pt-2">
                <FieldRow label="Property">
                  <Select value={lForm.property_id} onValueChange={(v) => setLForm({ ...lForm, property_id: v })}>
                    <SelectTrigger data-testid="lease-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                    <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </FieldRow>
                <FieldRow label="Tenant">
                  <Select value={lForm.tenant_id} onValueChange={(v) => setLForm({ ...lForm, tenant_id: v })}>
                    <SelectTrigger data-testid="lease-tenant-select"><SelectValue placeholder="Select tenant" /></SelectTrigger>
                    <SelectContent>{tenants.map(t => <SelectItem key={t.id} value={t.id}>{fullName(t)} — {t.email}</SelectItem>)}</SelectContent>
                  </Select>
                </FieldRow>
                <div className="grid grid-cols-2 gap-3">
                  <FieldRow label="Monthly rent ($)"><Input type="number" required value={lForm.monthly_rent} onChange={e => setLForm({ ...lForm, monthly_rent: e.target.value })} /></FieldRow>
                  <FieldRow label="Deposit ($)"><Input type="number" value={lForm.deposit} onChange={e => setLForm({ ...lForm, deposit: e.target.value })} /></FieldRow>
                  <FieldRow label="Start date"><Input type="date" required value={lForm.start_date} onChange={e => setLForm({ ...lForm, start_date: e.target.value })} /></FieldRow>
                  <FieldRow label="End date"><Input type="date" value={lForm.end_date} onChange={e => setLForm({ ...lForm, end_date: e.target.value })} /></FieldRow>
                </div>
                <DialogFooter><Button data-testid="lease-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">Create Lease</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-tenant-button" className="bg-slate-900 hover:bg-slate-800 gap-2"><UserPlus className="h-4 w-4" /> Add Tenant</Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Add a tenant — full demographics</DialogTitle></DialogHeader>
              <form onSubmit={createTenant} className="space-y-5 pt-2">
                <Section title="Account credentials">
                  <Grid>
                    <FieldRow label="Email *" col><Input data-testid="tenant-email" type="email" required value={tForm.email} onChange={e => setTForm({ ...tForm, email: e.target.value })} /></FieldRow>
                    <FieldRow label="Initial password *" col><Input data-testid="tenant-password" type="text" required minLength={6} value={tForm.password} onChange={e => setTForm({ ...tForm, password: e.target.value })} /></FieldRow>
                  </Grid>
                </Section>
                <Section title="Personal info">
                  <Grid>
                    <FieldRow label="First name *"><Input data-testid="tenant-first-name" required value={tForm.first_name} onChange={e => setTForm({ ...tForm, first_name: e.target.value })} /></FieldRow>
                    <FieldRow label="Last name *"><Input data-testid="tenant-last-name" required value={tForm.last_name} onChange={e => setTForm({ ...tForm, last_name: e.target.value })} /></FieldRow>
                    <FieldRow label="Date of birth"><Input type="date" value={tForm.date_of_birth} onChange={e => setTForm({ ...tForm, date_of_birth: e.target.value })} /></FieldRow>
                    <FieldRow label="Age"><Input type="number" value={tForm.age} onChange={e => setTForm({ ...tForm, age: e.target.value })} /></FieldRow>
                    <FieldRow label="Preferred language">
                      <Select value={tForm.preferred_language} onValueChange={(v) => setTForm({ ...tForm, preferred_language: v })}>
                        <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </FieldRow>
                  </Grid>
                </Section>
                <Section title="Identity documents (kept private)">
                  <Grid>
                    <FieldRow label="Social Security #"><Input value={tForm.social_security_number} onChange={e => setTForm({ ...tForm, social_security_number: e.target.value })} placeholder="XXX-XX-XXXX" /></FieldRow>
                    <FieldRow label="Passport #"><Input value={tForm.passport_number} onChange={e => setTForm({ ...tForm, passport_number: e.target.value })} /></FieldRow>
                  </Grid>
                </Section>
                <Section title="Contact">
                  <Grid>
                    <FieldRow label="Phone (home)"><Input value={tForm.phone} onChange={e => setTForm({ ...tForm, phone: e.target.value })} /></FieldRow>
                    <FieldRow label="Cell phone"><Input value={tForm.cell_phone} onChange={e => setTForm({ ...tForm, cell_phone: e.target.value })} /></FieldRow>
                    <FieldRow label="Home address" col><Input value={tForm.address} onChange={e => setTForm({ ...tForm, address: e.target.value })} /></FieldRow>
                  </Grid>
                </Section>
                <Section title="Employment">
                  <Grid>
                    <FieldRow label="Workplace name"><Input value={tForm.work_place_name} onChange={e => setTForm({ ...tForm, work_place_name: e.target.value })} /></FieldRow>
                    <FieldRow label="Work phone"><Input value={tForm.work_phone} onChange={e => setTForm({ ...tForm, work_phone: e.target.value })} /></FieldRow>
                    <FieldRow label="Work address" col><Input value={tForm.work_address} onChange={e => setTForm({ ...tForm, work_address: e.target.value })} /></FieldRow>
                    <FieldRow label="Salary ($/hour)"><Input type="number" step="0.01" value={tForm.work_salary_hourly} onChange={e => setTForm({ ...tForm, work_salary_hourly: e.target.value })} /></FieldRow>
                  </Grid>
                </Section>
                <Section title="Notes">
                  <Textarea rows={3} value={tForm.notes} onChange={e => setTForm({ ...tForm, notes: e.target.value })} placeholder="Anything else worth remembering…" />
                </Section>
                <DialogFooter><Button data-testid="tenant-submit" type="submit" className="bg-slate-900 hover:bg-slate-800">Create Tenant</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      }
    >
      <Card className="border-slate-200 mb-6">
        <div className="p-5 border-b border-slate-100 flex items-center justify-between gap-4">
          <h3 className="font-display font-semibold">All Tenants ({filtered.length})</h3>
          <div className="relative w-64">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="tenant-search"
              placeholder="Search name, email, phone…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Workplace</TableHead>
              <TableHead>Language</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">{tenants.length === 0 ? "No tenants yet" : "No matches"}</TableCell></TableRow>
            ) : filtered.map(t => {
              const active = t.is_active !== false;
              return (
              <TableRow key={t.id} data-testid={`tenant-row-${t.id}`} className="cursor-pointer hover:bg-slate-50" onClick={() => openTenant(t)}>
                <TableCell>
                  <div className="font-medium">{fullName(t)}</div>
                  <div className="text-xs text-slate-500">{t.email}</div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {t.cell_phone || t.phone || "—"}
                </TableCell>
                <TableCell className="text-sm text-slate-600 max-w-[200px] truncate">{t.address || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{t.work_place_name || "—"}</TableCell>
                <TableCell className="text-sm text-slate-600">{t.preferred_language || "—"}</TableCell>
                <TableCell>
                  <span
                    data-testid={`tenant-status-${t.id}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 border ${
                      active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-rose-500"}`} />
                    {active ? "Active" : "Inactive"}
                  </span>
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      onClick={() => openTenant(t)}
                      variant="ghost" size="sm"
                      data-testid={`view-tenant-${t.id}`}
                      className="gap-1.5 text-slate-600 hover:text-slate-900"
                    >
                      <Eye className="h-3.5 w-3.5" /> View
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`tenant-actions-${t.id}`}>
                          <MoreVertical className="h-4 w-4 text-slate-500" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-slate-500">Admin actions</DropdownMenuLabel>
                        {active ? (
                          <DropdownMenuItem data-testid={`deactivate-tenant-${t.id}`} onClick={() => setActive(t, false)} className="gap-2 cursor-pointer">
                            <UserX className="h-4 w-4 text-amber-600" /> Deactivate account
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem data-testid={`activate-tenant-${t.id}`} onClick={() => setActive(t, true)} className="gap-2 cursor-pointer">
                            <UserCheck className="h-4 w-4 text-emerald-600" /> Activate account
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem data-testid={`change-password-${t.id}`} onClick={() => { setPwdTarget(t); setPwdValue(""); }} className="gap-2 cursor-pointer">
                          <KeyRound className="h-4 w-4 text-blue-600" /> Change password…
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem data-testid={`delete-tenant-${t.id}`} onClick={() => setDeleteTarget(t)} className="gap-2 cursor-pointer text-rose-600 focus:text-rose-700">
                          <Trash2 className="h-4 w-4" /> Delete account…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </Card>

      <Card className="border-slate-200">
        <div className="p-5 border-b border-slate-100"><h3 className="font-display font-semibold">Lease Assignments ({leases.length})</h3></div>
        <Table>
          <TableHeader><TableRow><TableHead>Property</TableHead><TableHead>Tenant</TableHead><TableHead>Rent</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {leases.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No leases yet</TableCell></TableRow>
            ) : leases.map(l => (
              <TableRow key={l.id} data-testid={`lease-row-${l.id}`}>
                <TableCell className="font-medium">{l.property?.name}</TableCell>
                <TableCell>{l.tenant?.name}</TableCell>
                <TableCell className="font-mono-num">${l.monthly_rent}</TableCell>
                <TableCell>{l.start_date}</TableCell>
                <TableCell>{l.end_date || "—"}</TableCell>
                <TableCell><button onClick={() => delLease(l.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Detail / Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && (setEditing(null), setEditMode(false))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="tenant-detail-dialog">
          {editing && eForm && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-between gap-4">
                  <span>{fullName(eForm)}</span>
                  {!editMode ? (
                    <Button onClick={() => setEditMode(true)} size="sm" data-testid="edit-tenant-button" className="bg-slate-900 hover:bg-slate-800 gap-1.5">
                      <Pencil className="h-3.5 w-3.5" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button onClick={() => { setEForm({ ...blankTenant, ...editing }); setEditMode(false); }} variant="outline" size="sm">Cancel</Button>
                      <Button data-testid="save-tenant-button" onClick={saveTenant} size="sm" className="bg-slate-900 hover:bg-slate-800">Save</Button>
                    </div>
                  )}
                </DialogTitle>
              </DialogHeader>

              {!editMode ? (
                <ViewMode tenant={eForm} />
              ) : (
                <EditMode form={eForm} setForm={setEForm} />
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Change password dialog */}
      <Dialog open={!!pwdTarget} onOpenChange={(o) => { if (!o) { setPwdTarget(null); setPwdValue(""); } }}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
          </DialogHeader>
          {pwdTarget && (
            <div className="space-y-4 pt-2">
              <div className="bg-slate-50 border border-slate-200 px-3 py-2 text-sm">
                <div className="font-medium text-slate-900">{fullName(pwdTarget)}</div>
                <div className="text-xs text-slate-500">{pwdTarget.email}</div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">New password</Label>
                <Input
                  data-testid="new-password-input"
                  type="text"
                  autoComplete="new-password"
                  minLength={6}
                  value={pwdValue}
                  onChange={(e) => setPwdValue(e.target.value)}
                  placeholder="At least 6 characters"
                />
                <p className="text-xs text-slate-500">The tenant will use this password the next time they sign in. Their existing session (if any) will continue until it expires.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setPwdTarget(null); setPwdValue(""); }}>Cancel</Button>
            <Button data-testid="save-password-button" onClick={changePassword} className="bg-slate-900 hover:bg-slate-800 gap-1.5">
              <KeyRound className="h-3.5 w-3.5" /> Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-tenant-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tenant account?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  This will permanently remove <strong>{fullName(deleteTarget)}</strong> ({deleteTarget.email}) and any associated lease assignments. Historical rent, maintenance and incident records are preserved.
                  <br /><br />
                  This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-tenant" onClick={deleteTenant} className="bg-rose-600 hover:bg-rose-700">
              Yes, delete account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

function ViewMode({ tenant }) {
  return (
    <div className="space-y-5 pt-2 text-sm">
      <ViewSection title="Personal">
        <ViewRow icon={Mail} label="Email" value={tenant.email} />
        <ViewRow icon={Cake} label="Date of birth" value={tenant.date_of_birth} extra={tenant.age ? `(${tenant.age}y)` : ""} />
        <ViewRow icon={Globe2} label="Preferred language" value={tenant.preferred_language} />
      </ViewSection>
      <ViewSection title="Identity (private)">
        <ViewRow label="Social Security #" value={tenant.social_security_number} masked />
        <ViewRow label="Passport #" value={tenant.passport_number} masked />
      </ViewSection>
      <ViewSection title="Contact">
        <ViewRow icon={Phone} label="Home phone" value={tenant.phone} />
        <ViewRow icon={Phone} label="Cell phone" value={tenant.cell_phone} />
        <ViewRow icon={MapPin} label="Home address" value={tenant.address} />
      </ViewSection>
      <ViewSection title="Employment">
        <ViewRow icon={Briefcase} label="Workplace" value={tenant.work_place_name} />
        <ViewRow icon={MapPin} label="Work address" value={tenant.work_address} />
        <ViewRow icon={Phone} label="Work phone" value={tenant.work_phone} />
        <ViewRow label="Salary" value={tenant.work_salary_hourly ? `$${Number(tenant.work_salary_hourly).toFixed(2)}/hr` : ""} />
      </ViewSection>
      {tenant.notes && (
        <ViewSection title="Notes">
          <p className="text-slate-700 whitespace-pre-wrap">{tenant.notes}</p>
        </ViewSection>
      )}
    </div>
  );
}

function EditMode({ form, setForm }) {
  const onF = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  return (
    <div className="space-y-5 pt-2">
      <Section title="Personal info">
        <Grid>
          <FieldRow label="First name"><Input value={form.first_name || ""} onChange={onF("first_name")} /></FieldRow>
          <FieldRow label="Last name"><Input value={form.last_name || ""} onChange={onF("last_name")} /></FieldRow>
          <FieldRow label="Date of birth"><Input type="date" value={form.date_of_birth || ""} onChange={onF("date_of_birth")} /></FieldRow>
          <FieldRow label="Age"><Input type="number" value={form.age ?? ""} onChange={onF("age")} /></FieldRow>
          <FieldRow label="Email"><Input type="email" value={form.email || ""} onChange={onF("email")} /></FieldRow>
          <FieldRow label="Preferred language">
            <Select value={form.preferred_language || ""} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{LANGUAGES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </FieldRow>
        </Grid>
      </Section>
      <Section title="Identity documents">
        <Grid>
          <FieldRow label="Social Security #"><Input value={form.social_security_number || ""} onChange={onF("social_security_number")} /></FieldRow>
          <FieldRow label="Passport #"><Input value={form.passport_number || ""} onChange={onF("passport_number")} /></FieldRow>
        </Grid>
      </Section>
      <Section title="Contact">
        <Grid>
          <FieldRow label="Home phone"><Input value={form.phone || ""} onChange={onF("phone")} /></FieldRow>
          <FieldRow label="Cell phone"><Input value={form.cell_phone || ""} onChange={onF("cell_phone")} /></FieldRow>
          <FieldRow label="Home address" col><Input value={form.address || ""} onChange={onF("address")} /></FieldRow>
        </Grid>
      </Section>
      <Section title="Employment">
        <Grid>
          <FieldRow label="Workplace name"><Input value={form.work_place_name || ""} onChange={onF("work_place_name")} /></FieldRow>
          <FieldRow label="Work phone"><Input value={form.work_phone || ""} onChange={onF("work_phone")} /></FieldRow>
          <FieldRow label="Work address" col><Input value={form.work_address || ""} onChange={onF("work_address")} /></FieldRow>
          <FieldRow label="Salary ($/hour)"><Input type="number" step="0.01" value={form.work_salary_hourly ?? ""} onChange={onF("work_salary_hourly")} /></FieldRow>
        </Grid>
      </Section>
      <Section title="Notes"><Textarea rows={3} value={form.notes || ""} onChange={onF("notes")} /></Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-3">{title}</div>
      {children}
    </div>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-2 gap-3">{children}</div>; }
function FieldRow({ label, children, col }) {
  return (
    <div className={`space-y-1.5 ${col ? "col-span-2" : ""}`}>
      <Label className="text-xs text-slate-700">{label}</Label>
      {children}
    </div>
  );
}
function ViewSection({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-4 first:border-t-0 first:pt-0">
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2.5">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function ViewRow({ icon: Icon, label, value, extra, masked }) {
  const display = !value ? <span className="text-slate-300">—</span> : (masked ? "•".repeat(Math.max(value.length - 4, 0)) + value.slice(-4) : value);
  return (
    <div className="flex items-start gap-3">
      <div className="w-32 text-xs text-slate-500 pt-0.5 flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5" />}{label}
      </div>
      <div className="flex-1 text-slate-800">{display}{extra && <span className="text-slate-400 ml-2">{extra}</span>}</div>
    </div>
  );
}
